/**
 * Thinking Loader Store
 *
 * Step-text only. Manages which thinking step phrase to display and cycles
 * through a randomised pool while a run is active.
 *
 * Visibility of the ThinkingLoader component is derived separately in
 * chatActivityStore / ThinkingLoader.tsx — this store has no visibility
 * responsibility and no DOM knowledge.
 *
 * Public API (unchanged call-sites in chatStore / conversationYjsStore):
 *   startWaiting(activity, threadId?)  — picks a step pool, starts cycling
 *   stopWaiting(threadId?)             — stops cycling, resets to step 0
 */

import { create } from 'zustand';
import { getThinkingSteps } from '../services/thinkingStepsService';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('ThinkingLoader', { enabled: false });

export interface ThinkingStep {
  text: string;
}

export interface ActivityContext {
  toolName?: string;
  documentName?: string;
  operation?: string;
  imagePrompt?: string;
}

interface ThinkingLoaderState {
  // Step text state
  thinkingSteps: ThinkingStep[];
  currentStepIndex: number;
  currentActivity: string | null;
  /** Tracks which thread "owns" the current cycling state so concurrent threads don't clobber each other. */
  activeThreadId: string | null;

  // Interval handles
  cycleIntervalId: NodeJS.Timeout | null;
  stepDuration: number;

  // Actions
  startWaiting: (activity: string, threadId?: string, context?: ActivityContext) => void;
  stopWaiting: (threadId?: string) => void;
  nextStep: () => void;
  reset: () => void;
}

export const useThinkingLoaderStore = create<ThinkingLoaderState>((set, get) => ({
  thinkingSteps: [],
  currentStepIndex: 0,
  currentActivity: null,
  activeThreadId: null,
  cycleIntervalId: null,
  stepDuration: 3500,

  /**
   * Load step pool for the given activity and start cycling.
   * Safe to call multiple times — clears any existing interval first.
   */
  startWaiting: (activity, threadId, _context?) => {
    const { cycleIntervalId } = get();
    if (cycleIntervalId) clearInterval(cycleIntervalId);

    log.log('[ThinkingLoader] startWaiting', { activity, threadId });

    const steps = getThinkingSteps(activity);

    set({
      thinkingSteps: steps,
      currentStepIndex: 0,
      currentActivity: activity,
      activeThreadId: threadId ?? null,
    });

    // First step ("Thinking...") stays for 2 s, then switches to 3.5 s cadence.
    const firstIntervalId = setInterval(() => {
      get().nextStep();
    }, 2000);

    set({ cycleIntervalId: firstIntervalId });
  },

  /**
   * Stop cycling and reset to step 0.
   * If threadId is given, only acts when it matches the active thread.
   */
  stopWaiting: (threadId?) => {
    const { cycleIntervalId, activeThreadId } = get();
    if (threadId && activeThreadId && activeThreadId !== threadId) {
      log.log('[ThinkingLoader] stopWaiting: skipping, belongs to different thread', { activeThreadId, threadId });
      return;
    }

    log.log('[ThinkingLoader] stopWaiting', { threadId });
    if (cycleIntervalId) clearInterval(cycleIntervalId);

    // Clear currentActivity too so a stale stage lead ("Researching"/"Planning")
    // can't resurface if the loader reappears during an execution gap — it falls
    // back to the default "Thinking" lead until the next startWaiting.
    set({
      cycleIntervalId: null,
      currentStepIndex: 0,
      activeThreadId: null,
      currentActivity: null,
    });
  },

  nextStep: () => {
    const { thinkingSteps, currentStepIndex, cycleIntervalId, stepDuration } = get();
    if (thinkingSteps.length === 0) return;

    const nextIndex = (currentStepIndex + 1) % thinkingSteps.length;

    // On the 0→1 transition, swap to the longer 3.5 s interval.
    if (currentStepIndex === 0 && nextIndex === 1) {
      if (cycleIntervalId) clearInterval(cycleIntervalId);
      const newId = setInterval(() => get().nextStep(), stepDuration);
      set({ cycleIntervalId: newId });
    }

    set({ currentStepIndex: nextIndex });
  },

  reset: () => {
    const { cycleIntervalId } = get();
    if (cycleIntervalId) clearInterval(cycleIntervalId);
    set({
      thinkingSteps: [],
      currentStepIndex: 0,
      currentActivity: null,
      activeThreadId: null,
      cycleIntervalId: null,
    });
    log.log('[ThinkingLoader] reset');
  },
}));
